"use client";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import {
  AnalyticsEvents,
  onTrackEvent,
  trackEvent,
} from "~/connectors/analytics.connector";


const FIRST_HEARTBEAT_MS = 5000; // 5 seconds
const ACTIVE_HEARTBEAT_MS = 10000; // 10 seconds
const IDLE_HEARTBEAT_MS = 5 * 60 * 1000; // 5 minutes
const LONG_IDLE_HEARTBEAT_MS = 60 * 60 * 1000; // 1 hour
const IDLE_TIME_MS = 25_000; // 25 seconds of no activity
const LONG_IDLE_TIME_MS = 15 * 60 * 1000; // 15 minutes of idle

/**
 * Combined hook to track user activity, heartbeats, idle state, and browser events
 *
 * Behavior:
 * - First heartbeat after FIRST_HEARTBEAT_MS of page load or navigation
 * - Subsequent heartbeats every ACTIVE_HEARTBEAT_MS when active
 * - Heartbeat timer resets on navigation (FIRST_HEARTBEAT_MS after navigation)
 * - Heartbeat timer resets on any tracking event
 * - Detects idle after IDLE_TIME_MS of no user interaction
 * - Emits active event when user returns from idle
 * - During idle, heartbeats continue every IDLE_HEARTBEAT_MS
 * - Activity detection includes keydown events
 * - Tracks browser back navigation (popstate event)
 * - Tracks app visibility changes (visibilitychange event)
 */
export function useActivityTracker() {
  const pathname = usePathname();
  const heartbeatIntervalRef = useRef<
    ReturnType<typeof setInterval> | undefined
  >(undefined);
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const isIdleRef = useRef(false);
  const isLongIdleRef = useRef(false);
  const longIdleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const isFirstHeartbeatRef = useRef(true);

  // Function to reset heartbeat timer - uses useCallback for stability
  const resetHeartbeatTimer = useCallback(() => {
    // Clear existing heartbeat timers
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Start new heartbeat cycle - heartbeat will fire FIRST_HEARTBEAT_MS after the trigger
    const interval = isLongIdleRef.current
      ? LONG_IDLE_HEARTBEAT_MS
      : isIdleRef.current
        ? IDLE_HEARTBEAT_MS
        : ACTIVE_HEARTBEAT_MS;
    heartbeatTimeoutRef.current = setTimeout(() => {
      trackEvent(AnalyticsEvents.HEARTBEAT);

      // Subsequent heartbeats
      heartbeatIntervalRef.current = setInterval(() => {
        trackEvent(AnalyticsEvents.HEARTBEAT);
      }, interval);
    }, interval);
  }, []);

  useEffect(() => {
    const emitHeartbeat = () => {
      trackEvent(AnalyticsEvents.HEARTBEAT);
    };

    const emitIdle = () => {
      if (!isIdleRef.current) {
        isIdleRef.current = true;
        trackEvent(AnalyticsEvents.USER_IDLE, {
          idleDuration: IDLE_TIME_MS,
        });

        // Switch to longer heartbeat interval during idle
        resetHeartbeatTimer();

        // After 15 minutes of idle, drop heartbeat to 1 hour
        longIdleTimeoutRef.current = setTimeout(() => {
          isLongIdleRef.current = true;
          resetHeartbeatTimer();
        }, LONG_IDLE_TIME_MS);
      }
    };

    const emitActive = () => {
      if (isIdleRef.current) {
        isIdleRef.current = false;

        // Cancel long-idle escalation if it was pending or active
        if (longIdleTimeoutRef.current) {
          clearTimeout(longIdleTimeoutRef.current);
        }
        isLongIdleRef.current = false;

        trackEvent(AnalyticsEvents.USER_ACTIVE);

        // Switch back to normal heartbeat interval
        resetHeartbeatTimer();
      }
    };

    const resetIdleTimer = () => {
      // Clear existing idle timeout
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      // Emit active event if user was previously idle
      emitActive();

      // Set new idle timeout
      idleTimeoutRef.current = setTimeout(() => {
        emitIdle();
      }, IDLE_TIME_MS);
    };

    // Setup heartbeat mechanism
    const startHeartbeat = () => {
      // First heartbeat after 5 seconds
      heartbeatTimeoutRef.current = setTimeout(() => {
        emitHeartbeat();
        isFirstHeartbeatRef.current = false;

        // Subsequent heartbeats every 10 seconds (active state)
        heartbeatIntervalRef.current = setInterval(() => {
          emitHeartbeat();
        }, ACTIVE_HEARTBEAT_MS);
      }, FIRST_HEARTBEAT_MS);
    };

    // Browser event handlers for navigation and visibility
    const handlePopState = () => {
      trackEvent(AnalyticsEvents.APP_BACK);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        trackEvent(AnalyticsEvents.APP_HIDDEN);
      } else if (document.visibilityState === "visible") {
        trackEvent(AnalyticsEvents.APP_VISIBLE);
      }
    };

    // Events that indicate user activity (keydown covers all keyboard input, keypress is deprecated)
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, resetIdleTimer);
    });

    // Add browser event listeners
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("visibilitychange", handleVisibility);

    // Register callback to reset heartbeat timer on any tracking event
    const unregisterTrackEventCallback = onTrackEvent(() => {
      resetHeartbeatTimer();
    });

    // Start both mechanisms
    startHeartbeat();
    resetIdleTimer();

    // Cleanup
    return () => {
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (longIdleTimeoutRef.current) {
        clearTimeout(longIdleTimeoutRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetIdleTimer);
      });
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("visibilitychange", handleVisibility);
      unregisterTrackEventCallback();
    };
  }, [resetHeartbeatTimer]);

  // Reset heartbeat timer on navigation
  useEffect(() => {
    // Skip on initial mount (first heartbeat handles that)
    if (isFirstHeartbeatRef.current) {
      return;
    }

    // Navigation occurred, reset the heartbeat timer
    resetHeartbeatTimer();
  }, [pathname, resetHeartbeatTimer]);
}
